import { m } from '@pricedrop/i18n';
import { addMonths, format } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { he } from 'date-fns/locale/he';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ApiError } from '@/lib/api';
import { useLocale } from '@/lib/i18n';
import type {
  CreateNotificationBody,
  NotificationsDataClient,
  UpdateNotificationBody,
} from '@/lib/notifications/data-client';
import type { Notification, NotificationType } from '@/lib/notifications/types';
import {
  EXPIRATION_OPTIONS,
  NOTIFICATION_TYPE_OPTIONS,
  requiresPriceThreshold,
} from '@/lib/notifications/utils';

export interface NotificationFormProps {
  itemUin: number;
  itemName: string;
  currentPrice: number | null;
  existingNotification: Notification | null;
  dataClient: NotificationsDataClient;
  onSuccess?: (notification: Notification) => void;
  onDelete?: () => void;
  onCancel?: () => void;
  /**
   * `dialog` — rendered inside a ResponsiveDialog; dialog chrome (cancel
   *            button) comes from the wrapper, so we skip it here.
   * `embed`  — standalone (iframe on ksp.co.il); render our own cancel
   *            button when `onCancel` is supplied.
   */
  variant?: 'dialog' | 'embed';
}

interface NotificationFormValues {
  type: NotificationType;
  priceThreshold: number;
  expiresAt: Date | null;
}

/**
 * Defaults for scope='ITEM' only. TAGS-scope defaults live in
 * `item-notification-dialog.tsx` (M3 will fold that path into here).
 */
function computeDefaults(currentPrice: number | null, existingNotification: Notification | null) {
  const defaultType: NotificationType =
    existingNotification?.type ??
    (currentPrice == null || currentPrice <= 0 ? 'BACK_IN_STOCK' : 'PRICE_DROP');

  let defaultThreshold: number;
  if (existingNotification?.priceThreshold != null && currentPrice != null && currentPrice > 0) {
    defaultThreshold = Math.min(existingNotification.priceThreshold, currentPrice);
  } else if (currentPrice != null && currentPrice > 0) {
    defaultThreshold = Math.floor(currentPrice * 0.8);
  } else {
    defaultThreshold = 100;
  }

  const defaultExpiresAt: Date = existingNotification?.expiresAt
    ? new Date(existingNotification.expiresAt as string)
    : addMonths(new Date(), 3);

  return { defaultType, defaultThreshold, defaultExpiresAt };
}

export function NotificationForm({
  itemUin,
  itemName,
  currentPrice,
  existingNotification,
  dataClient,
  onSuccess,
  onDelete,
  onCancel,
  variant = 'dialog',
}: NotificationFormProps) {
  const isEditing = !!existingNotification;
  const [locale] = useLocale();
  // Map our app locale to a date-fns locale. `locale` is a `SupportedLocale`
  // tag (e.g. `'en-US' | 'he-IL'`); match on the language prefix.
  const dateFnsLocale = locale.startsWith('he') ? he : enUS;
  const { defaultType, defaultThreshold, defaultExpiresAt } = React.useMemo(
    () => computeDefaults(currentPrice, existingNotification),
    [currentPrice, existingNotification],
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<NotificationFormValues>({
    defaultValues: {
      type: defaultType,
      priceThreshold: defaultThreshold,
      expiresAt: defaultExpiresAt,
    },
  });

  const [submitting, setSubmitting] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

  const isLoading = isSubmitting || submitting || deleting;
  const isInStock = currentPrice != null && currentPrice > 0;
  const watchedType = watch('type');
  const showSlider = requiresPriceThreshold(watchedType) && isInStock;

  const availableTypeOptions = React.useMemo(
    () =>
      NOTIFICATION_TYPE_OPTIONS.filter((option) => {
        if (option.value === 'BACK_IN_STOCK') return !isInStock;
        if (
          option.value === 'PRICE_BELOW_THRESHOLD' ||
          option.value === 'LOWEST_PRICE' ||
          option.value === 'PRICE_DROP'
        )
          return isInStock;
        return true; // ANY_CHANGE always available
      }),
    [isInStock],
  );

  // Reset when `existingNotification` identity changes (e.g. dialog re-opened
  // with a different row). Use the defaults memo as trigger.
  React.useEffect(() => {
    reset({
      type: defaultType,
      priceThreshold: defaultThreshold,
      expiresAt: defaultExpiresAt,
    });
    setShowDeleteConfirmation(false);
  }, [defaultType, defaultThreshold, defaultExpiresAt, reset]);

  async function onSubmit(values: NotificationFormValues) {
    setSubmitting(true);
    try {
      let result: Notification;
      if (isEditing && existingNotification) {
        const body: UpdateNotificationBody = {
          type: values.type,
          priceThreshold: requiresPriceThreshold(values.type) ? values.priceThreshold : undefined,
          expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
        };
        result = await dataClient.update(existingNotification.id, body);
        toast.success(m.notifications_toast_updated());
      } else {
        const body: CreateNotificationBody = {
          scope: 'ITEM',
          type: values.type,
          itemUin,
          priceThreshold: requiresPriceThreshold(values.type) ? values.priceThreshold : undefined,
          expiresAt: values.expiresAt?.toISOString(),
        };
        result = await dataClient.create(body);
        toast.success(m.notifications_toast_created());
      }
      onSuccess?.(result);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'CONFLICT') {
        toast.info(m.notifications_toast_duplicate());
        return;
      }
      toast.error(m.notifications_toast_error());
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!existingNotification) return;
    setDeleting(true);
    try {
      await dataClient.remove(existingNotification.id);
      toast.success(m.notifications_toast_deleted());
      onDelete?.();
    } catch {
      toast.error(m.notifications_toast_error());
    } finally {
      setDeleting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="rounded-lg bg-muted/50 p-3">
        <p className="font-medium text-sm">{itemName}</p>
        <p className="text-muted-foreground text-xs">
          {m.notifications_item_number()}
          {itemUin}
        </p>
        {currentPrice != null && currentPrice > 0 && (
          <p className="mt-1 font-semibold text-sm">
            {m.notifications_threshold_current()}: ₪{currentPrice.toLocaleString()}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>{m.notifications_type_label()}</Label>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {showSlider && (
        <div className="space-y-3">
          <Label>{m.notifications_threshold_label()}</Label>
          {currentPrice != null ? (
            <Controller
              control={control}
              name="priceThreshold"
              render={({ field }) => (
                <div className="space-y-3">
                  <p className="text-center text-2xl font-bold">₪{field.value.toLocaleString()}</p>
                  <Slider
                    min={1}
                    max={currentPrice}
                    step={1}
                    value={[field.value]}
                    onValueChange={(vals) => field.onChange(vals[0])}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>₪1</span>
                    <span>₪{currentPrice.toLocaleString()}</span>
                  </div>
                </div>
              )}
            />
          ) : null}
          <p className="text-xs text-muted-foreground">{m.notifications_threshold_hint()}</p>
        </div>
      )}

      <div className="space-y-3">
        <Label>{m.notifications_expiration_label()}</Label>
        <div className="flex w-full items-center gap-2 rounded-lg border border-input bg-background p-3 text-sm">
          <span>
            {watch('expiresAt')
              ? format(watch('expiresAt') as Date, 'PPP', { locale: dateFnsLocale })
              : ''}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {EXPIRATION_OPTIONS.map((option) => {
            const targetDate = addMonths(new Date(), option.months);
            const currentExpiresAt = watch('expiresAt');
            const isActive =
              currentExpiresAt &&
              Math.abs(currentExpiresAt.getTime() - targetDate.getTime()) < 24 * 60 * 60 * 1000;
            return (
              <Button
                key={option.months}
                type="button"
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                className="h-10"
                onClick={() => setValue('expiresAt', targetDate)}
              >
                {option.label()}
              </Button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">{m.notifications_expiration_hint()}</p>
      </div>

      {showDeleteConfirmation ? (
        <div className="space-y-4 rounded-lg border-2 border-destructive/20 bg-destructive/10 p-4">
          <div className="text-center">
            <p className="mb-2 font-semibold text-destructive">
              {m.notifications_delete_confirm_title()}
            </p>
            <p className="text-sm text-muted-foreground">
              {m.notifications_delete_confirm_description()}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteConfirmation(false)}
              disabled={isLoading}
              className="flex-1"
            >
              {m.notifications_delete_confirm_cancel()}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isLoading}
              className="flex-1"
            >
              {deleting
                ? m.notifications_delete_deleting()
                : m.notifications_delete_confirm_delete()}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <Button type="submit" disabled={isLoading} className="w-full rounded-lg py-3 font-medium">
            {submitting || isSubmitting
              ? m.notifications_submit_saving()
              : isEditing
                ? m.notifications_submit_update()
                : m.notifications_submit_create()}
          </Button>
          {isEditing && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteConfirmation(true)}
              disabled={isLoading}
              className="w-full rounded-lg border-destructive py-3 font-medium text-destructive hover:bg-destructive hover:text-destructive-foreground"
            >
              {m.notifications_delete_button()}
            </Button>
          )}
          {variant === 'embed' && onCancel && (
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={isLoading}
              className="w-full"
            >
              {m.notifications_delete_confirm_cancel()}
            </Button>
          )}
        </div>
      )}
    </form>
  );
}
